import { containsWholeWordPhrase, foldForMatching, removePhrases } from './reserved-term-lexicon';

/**
 * Deterministic composition lexicon for the pastry rule pack (fase 2): it maps
 * recipe-line names to ingredient roles (appendice legale §4.1) and product
 * names to denomination roles (appendice legale §4.2), so the rule-pack DSL
 * never parses free text (ogni «il testo contiene…» è un fatto dell'adapter).
 *
 * Same infrastructure as `reserved-term-lexicon.ts`: fold + whole-word
 * matching, with exception locutions removed before matching and priority by
 * entry order — the first matching role wins, so the more specific role must
 * come first (COCOA_BUTTER before BUTTER, EGG_YOLK/EGG_WHITE before EGG,
 * CHEMICAL_LEAVENING before BREWERS_YEAST, VANILLIN before VANILLA_FLAVOURING,
 * CANDIED_CITRUS_PEEL before CANDIED_FRUIT, APRICOT_KERNEL before ALMOND,
 * CHOCOLATE_SUBSTITUTE before CHOCOLATE, CHOCOLATE before COCOA, FLAVOURING
 * last).
 */

/** Closed ingredient-role enum: appendix §4.1 plus redirects, extras and aggregates. */
export const COMPOSITION_ROLES = [
  'WHEAT_FLOUR',
  'OTHER_SUGAR',
  'SUGAR',
  'COCOA_BUTTER',
  'BUTTER',
  'EGG_YOLK',
  'EGG_WHITE',
  'EGG',
  'RAISIN',
  'CANDIED_CITRUS_PEEL',
  'CANDIED_FRUIT',
  'SOURDOUGH_INACTIVE',
  'SOURDOUGH',
  'CHEMICAL_LEAVENING',
  'BREWERS_YEAST',
  'APRICOT_KERNEL',
  'ALMOND',
  'CHOCOLATE_SUBSTITUTE',
  'CHOCOLATE',
  'COCOA',
  'VEGETABLE_FAT_NON_COCOA',
  'VANILLIN',
  'VANILLA_FLAVOURING',
  'VANILLA_POD',
  'ICE_CREAM',
  'JAM',
  'SPICE',
  'FLAVOURING',
  'RAISIN_PLUS_CANDIED_PEEL',
  'ALMOND_OR_KERNEL',
] as const;

export type CompositionRole = (typeof COMPOSITION_ROLES)[number];

/** Closed name-role enum: appendix §4.2. */
export const COMPOSITION_NAME_ROLES = [
  'PANETTONE',
  'PANDORO',
  'COLOMBA',
  'SAVOIARDO',
  'AMARETTO',
  'AMARETTO_MORBIDO',
  'CHOCOLATE_PRODUCT_ALLEGATO_I',
  'AL_CIOCCOLATO',
  'PURE_CHOCOLATE',
  'QUICK_FROZEN',
  'FROZEN',
  'ICE_CREAM',
  'ARTISAN',
  'VANILLA_NAME',
  'JAM_MARMELLATA',
] as const;

export type CompositionNameRole = (typeof COMPOSITION_NAME_ROLES)[number];

/** Head roles reserved by D.M. 22/7/2005 (coordinato con D.M. 16/5/2017). */
export const DM2005_NAME_ROLES = [
  'PANETTONE',
  'PANDORO',
  'COLOMBA',
  'SAVOIARDO',
  'AMARETTO',
  'AMARETTO_MORBIDO',
] as const satisfies readonly CompositionNameRole[];

/**
 * Allegato I chocolate head terms exempt from the «cacao: … % min» declaration
 * (art. 3 §3 dir. 2000/36/CE come recepita dall'art. 5 D.Lgs. 178/2003:
 * l'obbligo copre A.2 c/d — cioccolato in polvere e cacao zuccherato — e i
 * paragrafi 3, 4, 5, 8, 9; esenti il cioccolato bianco A.6, il cioccolato
 * ripieno A.7 e le praline/cioccolatini A.10, più i cacao puri in polvere
 * A.2 a/b che dichiarano il solo burro di cacao, art. 3 §4).
 * Correzione post-review 2026-09-06: «cioccolato in polvere» NON è esente.
 */
export const CHOCOLATE_ALLEGATO_I_WHITE_OR_POWDER_TERMS = [
  'cioccolato bianco',
  'cacao in polvere',
  'cacao magro',
  'cioccolato ripieno',
  'pralina',
  'praline',
  'cioccolatino',
  'cioccolatini',
] as const;

/** L. 443/1985 art. 5 c.8 (novellato da L. 34/2026 art. 16): artisan terms. */
export const ARTISAN_TERMS = [
  'artigianale',
  'artigianali',
  'artigiano',
  'artigiana',
  'artigianato',
  'artigianalmente',
] as const;

/** Agrumi del lessico «marmellata» (All. I D.Lgs. 50/2004), appendice §4.2-R8. */
export const CITRUS_FRUIT_TERMS = [
  'arancia',
  'arance',
  'arancia amara',
  'arance amare',
  'limone',
  'limoni',
  'cedro',
  'mandarino',
  'mandarini',
  'pompelmo',
  'bergamotto',
  'lime',
  'chinotto',
  'agrumi',
  'clementine',
] as const;

/** Fruits a jam/confettura can name, citrus first (R8 `jam.fruitNamed`). */
export const JAM_FRUIT_TERMS = [
  ...CITRUS_FRUIT_TERMS,
  'albicocca',
  'albicocche',
  'fragola',
  'fragole',
  'ciliegia',
  'ciliegie',
  'pesca',
  'pesche',
  'mela',
  'mele',
  'pera',
  'pere',
  'prugna',
  'prugne',
  'susina',
  'susine',
  'lampone',
  'lamponi',
  'mora',
  'more',
  'mirtillo',
  'mirtilli',
  'ribes',
  'uva',
  'ananas',
  'banana',
  'kiwi',
  'mango',
  'papaya',
  'fico',
  'fichi',
  'marrone',
  'marroni',
  'castagna',
  'castagne',
] as const;

interface RoleTerm {
  role: CompositionRole;
  terms: readonly string[];
  /** Locutions in which a term is NOT this role (the redirect target wins). */
  exceptions: readonly string[];
}

/**
 * Priority order = entry order: the first matching role wins. Each entry's
 * exceptions are subtracted from the haystack before matching, so a redirect
 * target listed BEFORE the generic entry plus an exception on the generic one
 * routes the line to the specific role.
 */
const ROLE_TERMS: readonly RoleTerm[] = [
  {
    role: 'COCOA_BUTTER',
    terms: ['burro di cacao'],
    exceptions: [],
  },
  {
    role: 'EGG_YOLK',
    terms: [
      'tuorlo',
      "tuorlo d'uovo",
      'tuorli',
      "tuorli d'uovo",
      'tuorlo pastorizzato',
      'tuorli pastorizzati',
    ],
    exceptions: [],
  },
  {
    role: 'EGG_WHITE',
    terms: ['albume', "albume d'uovo", 'albumi', "albumi d'uovo"],
    exceptions: [],
  },
  {
    role: 'EGG',
    terms: [
      'uovo',
      'uova',
      'uova intere',
      'uova di gallina',
      'uova fresche',
      'uova pastorizzate',
      'uova liquide',
    ],
    exceptions: ['tuorlo', 'tuorli', "tuorlo d'uovo", 'albume', 'albumi', "albume d'uovo"],
  },
  {
    role: 'OTHER_SUGAR',
    terms: [
      'zucchero invertito',
      'sciroppo di glucosio',
      'glucosio',
      'fruttosio',
      'sciroppo di glucosio-fruttosio',
    ],
    exceptions: [],
  },
  {
    role: 'SUGAR',
    terms: ['zucchero', 'saccarosio', 'zucchero semolato', 'zucchero a velo', 'zucchero di canna'],
    exceptions: [
      'zucchero invertito',
      'sciroppo di glucosio',
      'glucosio',
      'fruttosio',
      'sciroppo di glucosio-fruttosio',
    ],
  },
  {
    role: 'BUTTER',
    terms: ['burro', 'burro concentrato', 'burro anidro', 'burro chiarificato'],
    exceptions: ['burro di cacao', 'burro di arachidi', 'burro di mandorle', 'burro vegetale'],
  },
  {
    role: 'RAISIN',
    terms: ['uvetta', 'uva passa', 'uva sultanina', 'uvetta sultanina', 'zibibbo', 'zibibbo secco'],
    exceptions: [],
  },
  {
    role: 'CANDIED_CITRUS_PEEL',
    terms: [
      'scorze di agrumi candite',
      "scorza d'arancia candita",
      "scorze d'arancia candite",
      'scorza di limone candita',
      'limone candito',
      'arancia candita',
      'arance candite',
      'cedro candito',
      'cedri canditi',
      'scorzette candite',
      'canditi di agrumi',
      'cubetti di arancia candita',
      'cubetti di cedro candito',
      // In pasticceria le scorze di agrumi di una riga ricetta sono candite.
      "scorza d'arancia",
      "scorze d'arancia",
      'scorza di cedro',
      'scorze di cedro',
      'scorza di limone',
      'scorze di limone',
      'scorze di mandarino',
    ],
    exceptions: ['ciliegie candite', 'ciliegia candita'],
  },
  {
    role: 'CANDIED_FRUIT',
    terms: [
      'canditi',
      'candite',
      'candita',
      'candito',
      'frutta candita',
      'ciliegie candite',
      'ciliegia candita',
      'albicocche candite',
      'pere candite',
      'marron glacé',
    ],
    exceptions: [],
  },
  {
    role: 'SOURDOUGH_INACTIVE',
    terms: [
      'lievito madre disidratato',
      'lievito madre essiccato',
      'lievito madre inattivo',
      'lievito madre essiccato inattivo',
      'lievito madre disidratato inattivo',
      'pasta madre disidratata',
    ],
    exceptions: [],
  },
  {
    role: 'SOURDOUGH',
    terms: [
      'lievito naturale',
      'lievito madre',
      'lievito madre vivo',
      'pasta madre',
      'pasta acida',
      'lievito naturale da pasta acida',
      'licoli',
      'li.co.li',
    ],
    exceptions: [
      'lievito madre disidratato',
      'lievito madre essiccato',
      'lievito madre inattivo',
      'pasta madre disidratata',
    ],
  },
  {
    role: 'CHEMICAL_LEAVENING',
    terms: [
      'lievito chimico',
      'lieviti chimici',
      'lievito in polvere',
      'lieviti in polvere',
      'agenti lievitanti',
      'agente lievitante',
      'bicarbonato',
      'bicarbonato di sodio',
      'cremor tartari',
      'ammoniaca per dolci',
    ],
    exceptions: [],
  },
  {
    role: 'BREWERS_YEAST',
    terms: [
      'lievito',
      'lievito di birra',
      'lievito compresso',
      'lievito secco',
      'lievito fresco',
      'saccharomyces cerevisiae',
    ],
    exceptions: [
      // Difesa in profondità: queste forme sono risolte dalle voci precedenti.
      'lievito naturale',
      'lievito madre',
      'pasta madre',
      'pasta acida',
      'licoli',
      'li.co.li',
      'lievito madre disidratato',
      'lievito chimico',
      'lievito in polvere',
      'agenti lievitanti',
      'agente lievitante',
      'bicarbonato',
    ],
  },
  {
    role: 'APRICOT_KERNEL',
    terms: [
      'armelline',
      'armellina',
      'mandorle di albicocca',
      'mandorla di albicocca',
      'noccioli di albicocca',
      'nocciolo di albicocca',
    ],
    exceptions: [],
  },
  {
    role: 'ALMOND',
    terms: [
      'mandorle',
      'mandorla',
      'mandorle dolci',
      'mandorle pelate',
      'mandorle tostate',
      'granella di mandorle',
      'farina di mandorle',
      'pasta di mandorle',
    ],
    exceptions: [
      'armelline',
      'armellina',
      'mandorle di albicocca',
      'mandorla di albicocca',
      'noccioli di albicocca',
      'nocciolo di albicocca',
      'aroma di mandorla',
      'aroma di mandorle',
      'aroma mandorla',
      'aroma mandorle',
      'essenza di mandorla',
      'essenza di mandorle',
    ],
  },
  {
    role: 'CHOCOLATE_SUBSTITUTE',
    terms: [
      'surrogato di cioccolato',
      'surrogati di cioccolato',
      'composto di cioccolato',
      'composti di cioccolato',
    ],
    exceptions: [],
  },
  {
    role: 'CHOCOLATE',
    terms: ['cioccolato', 'cioccolati', 'gianduia', 'gianduiotto'],
    exceptions: [
      'aroma cioccolato',
      'aroma di cioccolato',
      'aromi di cioccolato',
      'surrogato di cioccolato',
      'surrogati di cioccolato',
      'composto di cioccolato',
      'composti di cioccolato',
    ],
  },
  {
    role: 'COCOA',
    terms: [
      'cacao',
      'cacao in polvere',
      'cacao magro',
      'cacao amaro',
      'pasta di cacao',
      'massa di cacao',
    ],
    exceptions: ['burro di cacao'],
  },
  {
    role: 'VEGETABLE_FAT_NON_COCOA',
    terms: [
      'margarina',
      'olio di girasole',
      'olio di palma',
      'olio di colza',
      'olio di semi',
      'olio di oliva',
      'olio extravergine di oliva',
      'grasso vegetale',
      'grassi vegetali',
      'olio vegetale',
      'oli vegetali',
      'burro vegetale',
      'shortening',
    ],
    exceptions: ['burro di cacao', 'olio essenziale', 'oli essenziali'],
  },
  {
    role: 'WHEAT_FLOUR',
    terms: [
      'farina di frumento',
      'farina di grano tenero',
      'farina di grano',
      'farina manitoba',
      'farina 00',
      'farina 0',
      'farina 1',
      'farina 2',
      'farina tipo 00',
      'farina tipo 0',
      'farina tipo 1',
      'farina tipo 2',
      'semola',
      'semola rimacinata',
    ],
    exceptions: [
      'farina di riso',
      'farina di mais',
      'farina di mandorle',
      'farina di castagne',
      'farina di ceci',
      'farina di avena',
      // Il grano saraceno non è grano tenero: senza glutine.
      'farina di grano saraceno',
      'farina senza glutine',
      'semola di mais',
      'semola di riso',
    ],
  },
  {
    role: 'VANILLIN',
    terms: ['vanillina', 'etilvanillina', 'aroma vanillina', 'aroma di vanillina'],
    exceptions: [],
  },
  {
    role: 'VANILLA_FLAVOURING',
    terms: [
      'aroma di vaniglia',
      'aroma naturale di vaniglia',
      'estratto di vaniglia',
      'aroma vaniglia',
      // L'appendice §4.1 elenca anche l'«aroma naturale» generico come vaniglia.
      'aroma naturale',
    ],
    exceptions: [],
  },
  {
    role: 'VANILLA_POD',
    terms: [
      'bacca di vaniglia',
      'bacche di vaniglia',
      'baccello di vaniglia',
      'baccelli di vaniglia',
      'semi di vaniglia',
      'vaniglia in bacche',
      'polvere di vaniglia',
      'vaniglia bourbon',
      'vaniglia tahiti',
      'vaniglia del madagascar',
    ],
    exceptions: [],
  },
  {
    role: 'ICE_CREAM',
    terms: [
      'gelato',
      'gelati',
      'semifreddo',
      'semifreddi',
      'sorbetto',
      'sorbetti',
      'mantecato',
      'mantecati',
    ],
    exceptions: ['cono', 'coni', 'biscotto gelato', 'biscotti gelato'],
  },
  {
    role: 'JAM',
    terms: [
      'marmellata',
      'marmellate',
      'confettura',
      'confetture',
      'confettura extra',
      'confetture extra',
      'gelatina di frutta',
      'crema di marroni',
      'crema di castagne',
    ],
    exceptions: [],
  },
  {
    role: 'SPICE',
    terms: [
      'cannella',
      'zenzero',
      'chiodi di garofano',
      'noce moscata',
      'cardamomo',
      'anice',
      'spezie',
      'spezia',
      'erbe aromatiche',
    ],
    exceptions: [],
  },
  {
    role: 'FLAVOURING',
    terms: [
      'aroma',
      'aromi',
      'aroma naturale',
      'aromi naturali',
      'essenza',
      'aromatizzante',
      'aromatizzanti',
    ],
    exceptions: [],
  },
];

/**
 * Italian recipe text uses both apostrophes ("tuorlo d'uovo" vs "d’uovo");
 * `foldForMatching` does not fold them, so every multi-word term carrying one
 * is matched in both spellings.
 */
const APOSTROPHES = ["'", '’'];

function apostropheVariants(term: string): readonly string[] {
  const variants = [term];
  for (const from of APOSTROPHES) {
    if (!term.includes(from)) {
      continue;
    }
    for (const to of APOSTROPHES) {
      const swapped = term.split(from).join(to);
      if (to !== from && !variants.includes(swapped)) {
        variants.push(swapped);
      }
    }
  }
  return variants;
}

interface PreparedRoleTerm {
  role: CompositionRole;
  terms: readonly string[];
  exceptions: readonly string[];
}

const PREPARED_ROLE_TERMS: readonly PreparedRoleTerm[] = ROLE_TERMS.map((entry) => ({
  role: entry.role,
  terms: entry.terms.flatMap((term) => apostropheVariants(term).map(foldForMatching)),
  exceptions: entry.exceptions.flatMap((term) => apostropheVariants(term).map(foldForMatching)),
}));

/** Role of a recipe-line ingredient name, or null when no role applies. */
export function resolveCompositionRole(effectiveName: string): CompositionRole | null {
  const haystack = foldForMatching(effectiveName);
  if (haystack.length === 0) {
    return null;
  }
  for (const entry of PREPARED_ROLE_TERMS) {
    const cleaned = removePhrases(haystack, entry.exceptions);
    if (entry.terms.some((term) => containsWholeWordPhrase(cleaned, term))) {
      return entry.role;
    }
  }
  return null;
}

interface NameRoleTerm {
  role: CompositionNameRole;
  terms: readonly string[];
}

const AMARETTO_MORBIDO_PHRASES = ['amaretto morbido', 'amaretti morbidi'] as const;

/**
 * Name-role terms, appendix §4.2. CHOCOLATE_PRODUCT_ALLEGATO_I is missing on
 * purpose: it fires only as the head of the denomination (`resolveNameHeadRole`),
 * never as a plain mention («torta al cioccolato» non è un prodotto di
 * cioccolato in tavoletta).
 */
const NAME_ROLE_TERMS: readonly NameRoleTerm[] = [
  {
    role: 'PANETTONE',
    terms: ['panettone', 'panettoni', 'panettoncino', 'panettoncini', 'panettonino', 'panettonini'],
  },
  { role: 'PANDORO', terms: ['pandoro', 'pandori', 'pandorino', 'pandorini'] },
  { role: 'COLOMBA', terms: ['colomba', 'colombe', 'colombina', 'colombine'] },
  { role: 'SAVOIARDO', terms: ['savoiardo', 'savoiardi', 'savoiardino', 'savoiardini'] },
  { role: 'AMARETTO_MORBIDO', terms: [...AMARETTO_MORBIDO_PHRASES] },
  { role: 'AMARETTO', terms: ['amaretto', 'amaretti'] },
  { role: 'AL_CIOCCOLATO', terms: ['al cioccolato', 'con cioccolato', 'di cioccolato'] },
  { role: 'PURE_CHOCOLATE', terms: ['puro cioccolato', 'cioccolato puro', 'puro cacao'] },
  { role: 'QUICK_FROZEN', terms: ['surgelato', 'surgelata', 'surgelati', 'surgelate'] },
  { role: 'FROZEN', terms: ['congelato', 'congelata', 'congelati', 'congelate'] },
  {
    role: 'ICE_CREAM',
    terms: [
      'gelato',
      'gelati',
      'torta gelato',
      'torte gelato',
      'semifreddo',
      'semifreddi',
      'sorbetto',
      'sorbetti',
    ],
  },
  { role: 'ARTISAN', terms: [...ARTISAN_TERMS] },
  { role: 'VANILLA_NAME', terms: ['vaniglia', 'alla vaniglia'] },
  { role: 'JAM_MARMELLATA', terms: ['marmellata', 'marmellate'] },
];

/**
 * Union of the denomination roles found in `legalName` and `commercialName`.
 * AMARETTO does not fire on a phrase where AMARETTO_MORBIDO fires.
 */
export function resolveNameRoles(
  legalName: string | null,
  commercialName: string | null,
): CompositionNameRole[] {
  const foldedTexts = [legalName, commercialName]
    .filter((text): text is string => typeof text === 'string')
    .map(foldForMatching)
    .filter((text) => text.length > 0);
  if (foldedTexts.length === 0) {
    return [];
  }
  // ' \n ' NON viene collassato dal fold: impedisce a un sintagma di cavalcare
  // la giunzione fra i due nomi ("... amaretto" + "Morbido al limone ...").
  const haystack = foldedTexts.join(' \n ');
  const found = new Set<CompositionNameRole>();
  for (const entry of NAME_ROLE_TERMS) {
    const target =
      entry.role === 'AMARETTO'
        ? removePhrases(haystack, AMARETTO_MORBIDO_PHRASES.map(foldForMatching))
        : haystack;
    if (entry.terms.some((term) => containsWholeWordPhrase(target, foldForMatching(term)))) {
      found.add(entry.role);
    }
  }
  return COMPOSITION_NAME_ROLES.filter((role) => found.has(role));
}

/** Articles and format adjectives skipped before reading the head token. */
const HEAD_SKIP_WORDS = new Set([
  'il',
  'lo',
  'la',
  'i',
  'gli',
  'le',
  'un',
  'uno',
  'una',
  'mini',
  'piccolo',
  'piccola',
  'piccoli',
  'piccole',
  'grande',
  'grandi',
  'maxi',
]);

const CHOCOLATE_HEAD: CompositionNameRole = 'CHOCOLATE_PRODUCT_ALLEGATO_I';

const HEAD_MULTIWORD_ROLES: ReadonlyMap<string, CompositionNameRole> = new Map([
  ['uovo di pasqua', CHOCOLATE_HEAD],
  ['uova di pasqua', CHOCOLATE_HEAD],
  ['uovo di cioccolato', CHOCOLATE_HEAD],
  ['uova di cioccolato', CHOCOLATE_HEAD],
  ['amaretto morbido', 'AMARETTO_MORBIDO'],
  ['amaretti morbidi', 'AMARETTO_MORBIDO'],
  ['torta gelato', 'ICE_CREAM'],
  ['torte gelato', 'ICE_CREAM'],
  ['puro cioccolato', CHOCOLATE_HEAD],
  ['cioccolato puro', CHOCOLATE_HEAD],
  ['puro cacao', CHOCOLATE_HEAD],
]);

const HEAD_TOKEN_ROLES: Readonly<Record<string, CompositionNameRole>> = {
  panettone: 'PANETTONE',
  panettoni: 'PANETTONE',
  panettoncino: 'PANETTONE',
  panettoncini: 'PANETTONE',
  panettonino: 'PANETTONE',
  panettonini: 'PANETTONE',
  pandoro: 'PANDORO',
  pandori: 'PANDORO',
  pandorino: 'PANDORO',
  pandorini: 'PANDORO',
  colomba: 'COLOMBA',
  colombe: 'COLOMBA',
  colombina: 'COLOMBA',
  colombine: 'COLOMBA',
  savoiardo: 'SAVOIARDO',
  savoiardi: 'SAVOIARDO',
  savoiardino: 'SAVOIARDO',
  savoiardini: 'SAVOIARDO',
  amaretto: 'AMARETTO',
  amaretti: 'AMARETTO',
  gelato: 'ICE_CREAM',
  gelati: 'ICE_CREAM',
  semifreddo: 'ICE_CREAM',
  semifreddi: 'ICE_CREAM',
  sorbetto: 'ICE_CREAM',
  sorbetti: 'ICE_CREAM',
  surgelato: 'QUICK_FROZEN',
  surgelata: 'QUICK_FROZEN',
  surgelati: 'QUICK_FROZEN',
  surgelate: 'QUICK_FROZEN',
  congelato: 'FROZEN',
  congelata: 'FROZEN',
  congelati: 'FROZEN',
  congelate: 'FROZEN',
  cioccolato: CHOCOLATE_HEAD,
  cioccolatino: CHOCOLATE_HEAD,
  cioccolatini: CHOCOLATE_HEAD,
  pralina: CHOCOLATE_HEAD,
  praline: CHOCOLATE_HEAD,
  gianduia: CHOCOLATE_HEAD,
  gianduiotto: CHOCOLATE_HEAD,
  cacao: CHOCOLATE_HEAD,
  marmellata: 'JAM_MARMELLATA',
  marmellate: 'JAM_MARMELLATA',
};

/**
 * Role of the first noun phrase of the legal name (after fold, skipping
 * leading articles and format adjectives): «Panettone classico» → PANETTONE,
 * «Torta di panettone» → null. Multiword heads are tried before the single
 * token so «Amaretti morbidi» resolves to AMARETTO_MORBIDO, not AMARETTO.
 */
export function resolveNameHeadRole(legalName: string | null): CompositionNameRole | null {
  if (legalName === null) {
    return null;
  }
  const folded = foldForMatching(legalName);
  if (folded.length === 0) {
    return null;
  }
  const tokens = folded.split(' ').filter((token) => token.length > 0);
  while (tokens.length > 0) {
    const first = tokens[0];
    const stripped = first.startsWith("l'")
      ? first.slice(2)
      : first.startsWith('l’')
        ? first.slice(2)
        : first;
    if (stripped.length === 0 || HEAD_SKIP_WORDS.has(stripped)) {
      tokens.shift();
      continue;
    }
    if (stripped !== first) {
      tokens[0] = stripped;
    }
    break;
  }
  if (tokens.length === 0) {
    return null;
  }
  const threeTokens = tokens.slice(0, 3).join(' ');
  const twoTokens = tokens.slice(0, 2).join(' ');
  const oneToken = tokens[0];
  return (
    HEAD_MULTIWORD_ROLES.get(threeTokens) ??
    HEAD_MULTIWORD_ROLES.get(twoTokens) ??
    HEAD_TOKEN_ROLES[oneToken] ??
    null
  );
}

/** «senza …» declarations in the denomination (art. 7 c.1-2, art. 8 c.2 D.M. 2005). */
const ABSENCE_PHRASES = {
  raisin: ['senza uvetta', 'senza uva passa', 'senza uva sultanina', 'senza zibibbo'],
  candiedPeel: [
    'senza canditi',
    'senza candite',
    'senza uvetta e canditi',
    'senza uvetta e candite',
    'senza uvetta né canditi',
    'senza uvetta né candite',
    'senza scorze candite',
    'senza scorzette candite',
    'senza scorze di agrumi candite',
  ],
  glaze: [
    'senza glassa',
    'senza glassatura',
    'senza decoro',
    'senza decori',
    'senza decorazioni',
    'non glassato',
    'non glassata',
    'non glassati',
    'non glassate',
  ],
} as const;

export interface NameAbsenceDeclaration {
  raisin: boolean;
  candiedPeel: boolean;
  glaze: boolean;
}

/** Which deroggable roles the denomination explicitly declares absent. */
export function nameDeclaresAbsence(legalName: string | null): NameAbsenceDeclaration {
  const haystack = legalName === null ? '' : foldForMatching(legalName);
  const has = (phrases: readonly string[]): boolean =>
    haystack.length > 0 &&
    phrases.some((phrase) => containsWholeWordPhrase(haystack, foldForMatching(phrase)));
  return {
    raisin: has(ABSENCE_PHRASES.raisin),
    candiedPeel: has(ABSENCE_PHRASES.candiedPeel),
    glaze: has(ABSENCE_PHRASES.glaze),
  };
}

/** True when the text names a citrus fruit (R8: «marmellata» riservata agli agrumi). */
export function isCitrusFruitName(text: string | null): boolean {
  const haystack = text === null ? '' : foldForMatching(text);
  if (haystack.length === 0) {
    return false;
  }
  return CITRUS_FRUIT_TERMS.some((term) =>
    containsWholeWordPhrase(haystack, foldForMatching(term)),
  );
}

/** The fruit a jam line or name names, folded, or null when none is named. */
export function namedFruitIn(text: string | null): string | null {
  const haystack = text === null ? '' : foldForMatching(text);
  if (haystack.length === 0) {
    return null;
  }
  const fruit = JAM_FRUIT_TERMS.find((term) =>
    containsWholeWordPhrase(haystack, foldForMatching(term)),
  );
  return fruit ?? null;
}

/**
 * The frozen-storage text facts run on already-folded text (the adapter passes
 * `foldForMatching(storageInstructions + ' ' + usageInstructions)`); folding
 * again here is idempotent and keeps raw label text working too.
 */
function foldedHaystack(text: string | null): string {
  return text === null ? '' : foldForMatching(text);
}

const REFREEZE_PHRASE = 'non ricongelare';

/** Art. 8 c.1 d) D.Lgs. 110/1992: «non ricongelare dopo lo scongelamento». */
export function textHasNoRefreezeWarning(foldedText: string | null): boolean {
  const haystack = foldedHaystack(foldedText);
  return haystack.length > 0 && containsWholeWordPhrase(haystack, REFREEZE_PHRASE);
}

const STORAGE_EQUIPMENT_TERMS = [
  'freezer',
  'freezers',
  'congelatore',
  'congelatori',
  'surgelatore',
  'surgelatori',
] as const;

/** Art. 8 c.1 c) D.Lgs. 110/1992: temperatura di conservazione o attrezzatura. */
export function textHasTemperatureOrEquipment(foldedText: string | null): boolean {
  const haystack = foldedHaystack(foldedText);
  if (haystack.length === 0) {
    return false;
  }
  // Accetta −(U+2212), – e - e spazi facoltativi: "-18 °C", "−18°C", "18 °c".
  if (/[−–-]?\d+\s?°\s?c/.test(haystack)) {
    return true;
  }
  return STORAGE_EQUIPMENT_TERMS.some((term) => containsWholeWordPhrase(haystack, term));
}

/** Durata di conservazione presso il consumatore (giorni/settimane/mesi/fino al TMC). */
const CONSUMER_PERIOD_PATTERN = /giorn|settiman|mes|fino al/;

/** Scomparto o attrezzatura presso il consumatore (art. 8 c.1 b) D.Lgs. 110). */
const CONSUMER_PLACE_PATTERN = /freezer|congelatore|frigo|scomparto|\*/;

/** Art. 8 c.1 b) D.Lgs. 110/1992: periodo per tipo di scomparto del freezer. */
export function textHasConsumerStoragePeriod(foldedText: string | null): boolean {
  const haystack = foldedHaystack(foldedText);
  if (haystack.length === 0) {
    return false;
  }
  return CONSUMER_PERIOD_PATTERN.test(haystack) && CONSUMER_PLACE_PATTERN.test(haystack);
}

export interface VanillaLineFlags {
  saysNatural: boolean;
  saysVanilla: boolean;
  saysNaturalVanilla: boolean;
}

const NATURAL_VANILLA_PHRASES = ['aroma naturale di vaniglia', 'aroma naturale vaniglia'] as const;
const NATURAL_PHRASES = ['aroma naturale', 'aromi naturali'] as const;
const VANILLA_PHRASES = [
  'aroma di vaniglia',
  'aroma vaniglia',
  'estratto di vaniglia',
  'essenza di vaniglia',
] as const;

/**
 * R6 (`EU.1334.ART16.VANILLA`) line flags: «aroma naturale di vaniglia» vs
 * «aroma di vaniglia» senza «naturale» vs «aroma naturale» generico.
 */
export function vanillaLineFlags(effectiveName: string | null): VanillaLineFlags {
  const haystack = foldedHaystack(effectiveName);
  const says = (phrases: readonly string[]): boolean =>
    haystack.length > 0 &&
    phrases.some((phrase) => containsWholeWordPhrase(haystack, foldForMatching(phrase)));
  const saysNaturalVanilla = says(NATURAL_VANILLA_PHRASES);
  return {
    saysNaturalVanilla,
    saysNatural: saysNaturalVanilla || says(NATURAL_PHRASES),
    // «aroma di vaniglia» vale come tale solo quando NON è «naturale» (R6 riga 4).
    saysVanilla: !saysNaturalVanilla && says(VANILLA_PHRASES),
  };
}

/** True when the text mentions the artisan terms of L. 443/1985 art. 5 c.8. */
export function textMentionsArtisan(text: string | null): boolean {
  const haystack = foldedHaystack(text);
  if (haystack.length === 0) {
    return false;
  }
  return ARTISAN_TERMS.some((term) => containsWholeWordPhrase(haystack, foldForMatching(term)));
}
